import { ChangeDetectionStrategy, Component, inject, signal, computed } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgOptimizedImage, CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideEye, lucideEyeOff } from '@ng-icons/lucide';
import { finalize } from 'rxjs/operators';
import { ZardButtonComponent } from '../../../shared/components/button';
import { ZardCardComponent } from '../../../shared/components/card';
import { ZardCheckboxComponent } from '../../../shared/components/checkbox';
import { ZardInputDirective } from '../../../shared/components/input';
import { UserService } from '../../../core/auth/services/user.service';
import { Errors } from '../../../core/models/errors.model';
import {UiLabel} from '../../../shared/components/label/label';

@Component({
  selector: 'app-auth-signup',
  standalone: true,
  imports: [CommonModule, FormsModule, NgOptimizedImage, NgIcon, ZardButtonComponent, ZardCardComponent, ZardCheckboxComponent, ZardInputDirective, UiLabel],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [provideIcons({ lucideEye, lucideEyeOff })],
  templateUrl: './auth-signup.component.html',
  styleUrl: './auth-signup.component.css',
})
export class AuthSignupComponent {
  protected readonly router = inject(Router);
  private readonly userService = inject(UserService);

  protected readonly name = signal('');
  protected readonly email = signal('');
  protected readonly password = signal('');
  protected readonly confirmPassword = signal('');
  protected readonly acceptedTerms = signal(false);

  protected readonly canSignup = computed(
    () =>
      !!this.email() &&
      !!this.password() &&
      !!this.confirmPassword() &&
      !!this.name() &&
      this.acceptedTerms() &&
      this.password() === this.confirmPassword() &&
      this.password().length >= 8,
  );

  protected readonly layoutOuter = 'min-h-screen flex flex-col md:flex-row bg-gray-50 md:bg-white';
  protected readonly sidePanelBgImage = 'https://images.unsplash.com/photo-1759863468440-85130c0dff28?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080';
  protected readonly layoutSidePanel =
    'hidden md:flex md:w-1/2 lg:w-5/12 relative overflow-hidden flex-col justify-between p-12 text-white';
   protected readonly layoutOverlay =
     'absolute inset-0 z-0 bg-gradient-to-t from-[#1A2E47] via-[#1A2E47]/50 to-transparent';

   protected readonly showPassword = signal(false);
   protected readonly showConfirmPassword = signal(false);
   protected readonly isSubmitting = signal(false);
   protected readonly errors = signal<Errors | null>(null);
    // tracks whether the user has attempted to submit the form so we can show validation messages
    protected readonly submitted = signal(false);

   /**
    * Format error messages based on server response type:
    * - 422 (Validation): Show field-specific errors
    * - 409 (Conflict): Show details message
    * - Other: Show generic errors object
    */
   protected readonly errorMessages = () => {
     const err = this.errors();
     if (!err) return [];

     const messages: string[] = [];

     // Case 1: Server validation errors (422) with fieldErrors
     if (err.fieldErrors && Object.keys(err.fieldErrors).length > 0) {
       for (const [field, fieldMessages] of Object.entries(err.fieldErrors)) {
         if (Array.isArray(fieldMessages)) {
           fieldMessages.forEach(msg => {
             messages.push(`${field}: ${msg}`);
           });
         }
       }
     }
     // Case 2: Conflict or other server errors (409, etc.) with details
     else if (err.details) {
       messages.push(err.details);
     }
     // Case 3: Generic errors object
     else if (err.errors && Object.keys(err.errors).length > 0) {
       Object.entries(err.errors).forEach(([field, message]) => {
         if (field === 'message' || field === 'network') {
           messages.push(message);
         } else {
           messages.push(`${field}: ${message}`);
         }
       });
     }

     return messages;
   };

  protected handleSignup(): void {
      // mark as submitted so validation messages appear in real-time after first attempt
      this.submitted.set(true);

      if (!this.canSignup() || this.isSubmitting()) {
        return;
      }

      this.errors.set(null);
      this.isSubmitting.set(true);

     this.userService
       .register({
         name: this.name().trim(),
         email: this.email().trim(),
         password: this.password(),
         passwordConfirmation: this.confirmPassword(),
       })
       .pipe(finalize(() => this.isSubmitting.set(false)))
       .subscribe({
          next: () => {
            // Backend now authenticates the user on register (returns tokens).
            // Keep email for confirmation screen display, but clear password fields.
            // Since the user is authenticated immediately, continue to the onboarding
            // profile setup step instead of a separate unauthenticated confirmation page.
            this.password.set('');
            this.confirmPassword.set('');
            void this.router.navigate(['/setup/confirmation']);
          },
         error: (err: Errors) => this.errors.set(err),
       });
  }

  /**
   * Real-time validation helpers for the template. Each returns an array of messages
   * to display under the corresponding input. They combine client-side checks and
   * server-side fieldErrors when present.
   */
  protected nameErrors(): string[] {
    const msgs: string[] = [];
    const val = this.name().trim();

    if ((this.submitted() || val.length > 0) && val.length === 0) {
      msgs.push('Le nom est requis.');
    }

    // server side errors
    const err = this.errors();
    if (err?.fieldErrors && err.fieldErrors['name']) {
      msgs.push(...err.fieldErrors['name']);
    }

    return msgs;
  }

  protected emailErrors(): string[] {
    const msgs: string[] = [];
    const val = this.email().trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (this.submitted() || val.length > 0) {
      if (val.length === 0) {
        msgs.push("L'adresse e-mail est requise.");
      } else if (!emailRegex.test(val)) {
        msgs.push("L'adresse e-mail n'est pas valide.");
      }
    }

    const err = this.errors();
    if (err?.fieldErrors && err.fieldErrors['email']) {
      msgs.push(...err.fieldErrors['email']);
    }

    return msgs;
  }

  protected passwordErrors(): string[] {
    const msgs: string[] = [];
    const val = this.password();

    if (this.submitted() || val.length > 0) {
      if (val.length === 0) {
        msgs.push('Le mot de passe est requis.');
      } else if (val.length < 8) {
        msgs.push('Le mot de passe doit contenir au moins 8 caractères.');
      }
    }

    const err = this.errors();
    if (err?.fieldErrors && err.fieldErrors['password']) {
      msgs.push(...err.fieldErrors['password']);
    }

    return msgs;
  }

  protected confirmPasswordErrors(): string[] {
    const msgs: string[] = [];
    const val = this.confirmPassword();

    if (this.submitted() || val.length > 0) {
      if (val.length === 0) {
        msgs.push("La confirmation du mot de passe est requise.");
      } else if (val !== this.password()) {
        msgs.push('Les mots de passe ne correspondent pas.');
      }
    }

    const err = this.errors();
    // server might use 'passwordConfirmation' as field name
    if (err?.fieldErrors && err.fieldErrors['passwordConfirmation']) {
      msgs.push(...err.fieldErrors['passwordConfirmation']);
    }

    return msgs;
  }

  protected acceptedTermsErrors(): string[] {
    const msgs: string[] = [];
    if (this.submitted() && !this.acceptedTerms()) {
      msgs.push("Vous devez accepter les termes et la politique de confidentialité.");
    }
    return msgs;
  }

  protected readonly navigator = navigator;
}
